import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import {
  useLoginMutation,
  useRegisterMutation,
  useResendVerificationMutation,
} from '@/shared/api/endpoints';
import { errorCode, errorDetails } from '@/shared/api/errors';
import { analytics } from '@/shared/lib/analytics';
import { useContentLocale } from '@/shared/lib/locale';
import { browser, isElsewhere } from '@/shared/lib/site';
import { Button } from '@/shared/ui/Button';
import { Alert, Card, Radio, TextField } from '@/shared/ui/primitives';

import { afterSignIn } from './destination';

const ROLES = ['student', 'teacher'] as const;
type ChosenRole = (typeof ROLES)[number];

/** Sign-in and registration share one form; `mode` decides the fields and the request. */
export function AuthForm({ mode }: { mode: 'login' | 'register' }) {
  const { t } = useTranslation();
  const locale = useContentLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [login, loginState] = useLoginMutation();
  const [register, registerState] = useRegisterMutation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<ChosenRole>('student');
  // Asked for once the server says the account has a second factor.
  const [code, setCode] = useState('');
  const [askCode, setAskCode] = useState(false);

  const state = mode === 'login' ? loginState : registerState;
  const failure = state.error ? (errorCode(state.error) ?? 'unknown') : null;
  // How long wrong passwords made the account wait, when they did.
  const wait = (errorDetails(state.error) as { retry_after_seconds?: number } | undefined)
    ?.retry_after_seconds;
  // The page that asked for the sign-in, if one did.
  // Where the sign-in was asked from: a page of another host says so in the address,
  // a page of this one in the router's state.
  const returnTo =
    new URLSearchParams(location.search).get('next') ??
    (location.state as { from?: string } | null)?.from;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (mode === 'register') {
      const asked = await register({ email, displayName: displayName.trim(), locale, role });
      if (asked.data) analytics.goal('registered');
      return;
    }
    const signedIn = await login({ email, password, code: askCode ? code : null });
    if (signedIn.error && errorCode(signedIn.error) === 'second_factor_required') setAskCode(true);
    // Back to where the sign-in was asked for, or to where the interface of this role starts.
    if (!signedIn.data) return;
    const to = afterSignIn(signedIn.data.user.role, returnTo);
    if (isElsewhere(to)) browser.leave(to);
    else await navigate(to, { replace: true });
  };

  // Asked for: the rest (the password) happens on the page the mailed link leads to. The
  // server answers the same for an address that already has an account, and so does this.
  if (mode === 'register' && registerState.data) {
    return (
      <Card className="mx-auto max-w-md space-y-4">
        <h1 className="text-2xl font-bold">{t('auth.confirm.title')}</h1>
        <p>{t('auth.confirm.sent', { email: registerState.data.email })}</p>
        <p className="text-sm text-muted">{t('auth.confirm.help')}</p>
        <ResendVerification email={registerState.data.email} />
        <p className="text-center text-sm">
          <Link to="/login" className="font-medium text-brand hover:underline">
            {t('auth.register.switchLink')}
          </Link>
        </p>
      </Card>
    );
  }

  return (
    <Card className="mx-auto max-w-md">
      <h1 className="mb-5 text-2xl font-bold">{t(`auth.${mode}.title`)}</h1>
      <form className="space-y-4" onSubmit={(event) => void onSubmit(event)}>
        {mode === 'register' ? (
          <TextField
            label={t('auth.displayName')}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            autoComplete="nickname"
            maxLength={80}
            required
          />
        ) : null}
        <TextField
          label={t('auth.email')}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          required
        />
        {mode === 'login' ? (
          <TextField
            label={t('auth.password')}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        ) : (
          <p className="text-sm text-muted">{t('auth.register.passwordLater')}</p>
        )}
        {mode === 'register' ? (
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium">{t('auth.role.title')}</legend>
            {ROLES.map((option) => (
              <Radio
                key={option}
                name="role"
                checked={role === option}
                onChange={() => setRole(option)}
                label={t(`roles.${option}`)}
                hint={t(`auth.role.${option}`)}
              />
            ))}
          </fieldset>
        ) : null}
        {mode === 'login' && askCode ? (
          <TextField
            label={t('auth.code.label')}
            hint={t('auth.code.hint')}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="one-time-code"
            inputMode="numeric"
            required
          />
        ) : null}
        {failure && !(failure === 'second_factor_required' && askCode && !code) ? (
          <Alert tone="bad" live>
            {t(`errors.${failure}`, {
              defaultValue: t('errors.unknown'),
              minutes: Math.max(1, Math.ceil((wait ?? 60) / 60)),
            })}
          </Alert>
        ) : null}
        {/* Signing in before the mail was opened: offer to send it again. */}
        {mode === 'login' && failure === 'email_not_verified' ? (
          <ResendVerification email={email} />
        ) : null}
        <Button type="submit" size="lg" className="w-full" disabled={state.isLoading}>
          {t(`auth.${mode}.submit`)}
        </Button>
      </form>
      {mode === 'login' ? (
        <p className="mt-4 text-center text-sm">
          <Link to="/forgot-password" className="font-medium text-brand hover:underline">
            {t('auth.forgot.link')}
          </Link>
        </p>
      ) : null}
      <p className="mt-5 text-center text-sm text-muted">
        {t(`auth.${mode}.switchPrompt`)}{' '}
        <Link
          to={mode === 'login' ? '/register' : '/login'}
          state={location.state}
          className="font-medium text-brand hover:underline"
        >
          {t(`auth.${mode}.switchLink`)}
        </Link>
      </p>
    </Card>
  );
}

/** Asks for the confirmation mail once more. The server never says whether it sent one. */
function ResendVerification({ email }: { email: string }) {
  const { t } = useTranslation();
  const [resend, state] = useResendVerificationMutation();
  return (
    <div className="space-y-2">
      <Button
        variant="secondary"
        className="w-full"
        disabled={state.isLoading}
        onClick={() => void resend(email)}
      >
        {t('auth.confirm.resend')}
      </Button>
      {state.isSuccess ? (
        <Alert tone="ok" live>
          {t('auth.confirm.resent')}
        </Alert>
      ) : null}
      {state.isError ? (
        <Alert tone="bad" live>
          {t('errors.unknown')}
        </Alert>
      ) : null}
    </div>
  );
}
